import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
    sendOTP,
    sendOTPViaCall,
    validateOTP,
    fetchAppConfig,
    getConsentContent,
    checkSsoEligible,
} from '../../store/actions/authActionCreator';
import {
    Box,
    makeStyles,
    Typography,
    Button,
    Paper,
    withStyles,
    Link,
    Fade,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    CircularProgress,
} from '@material-ui/core';
import OtpInput from 'react-otp-input';
import {
    GetOrganisations,
    StoreOrganisation,
} from '../../store/actions/settingsActionCreator';
import PhoneRoundedIcon from '@material-ui/icons/PhoneRounded';
import PhoneInTalkRoundedIcon from '@material-ui/icons/PhoneInTalkRounded';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import {
    isValidPhoneNumber,
    getCountryCallingCode,
    CountryCode,
} from 'libphonenumber-js';

import { API_URL } from '../../config/env/env';
import loginImage from '../../assets/images/loginImage.png';
import featureImage1 from '../../assets/images/featureImage1.png';
import featureImage2 from '../../assets/images/featureImage2.png';
import featureImage3 from '../../assets/images/featureImage3.png';
import featureImage4 from '../../assets/images/featureImage4.png';
import deAppImages from '../../assets/images/deAppImages.png';
import {
    getActiveInitRoute,
    getLoginUser,
    getUserToken,
    logout,
    showMessage,
} from '../../utils/helper';
import ConsentModal, { ConsentPara } from './ConsentModal';
import { captureLoginMpin, isMpinPending } from '../../utils/mpinGate';
import OtpOnCallModal, {
    copyCallNumber,
    formatCallNumber,
} from './OtpOnCallModal';
import {
    OnlineOnboarding,
    LocationTracking,
    StaffWithApp,
    DigitalPayroll,
    SmartAttendance,
} from '../../assets/svg';
import { BD_INTERNAL_ROLES } from '../../utils/appHelper';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Country {
    code: CountryCode;
    dialCode: string;
    name: string;
    flag: string;
}

interface ValidationState {
    isValid: boolean;
    message: string;
}

// ─── Country list (derived from libphonenumber-js + Intl.DisplayNames) ────────

// India + Middle East (GCC + Levant + North Africa) ISO 3166-1 alpha-2 codes
const REGION_CODES: CountryCode[] = [
    'IN', // India
    'AE',
    'SA',
    'QA',
    'KW',
    'BH',
    'OM', // GCC
    'JO',
    'IQ',
    'LB',
    'YE',
    'SY', // Levant / Arabian Peninsula
    'EG',
    'IR', // North Africa / Iran
];

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

// Derive flag emoji from ISO code using Unicode regional indicator symbols
const toFlag = (code: string): string =>
    code
        .toUpperCase()
        .replace(/./g, (c) => String.fromCodePoint(c.charCodeAt(0) + 127397));

const ALLOWED_COUNTRIES: Country[] = REGION_CODES.map((code) => ({
    code,
    dialCode: `+${getCountryCallingCode(code)}`,
    name: regionNames.of(code) ?? code,
    flag: toFlag(code),
})).sort((a, b) => {
    if (a.code === 'IN') return -1;
    if (b.code === 'IN') return 1;
    return a.name.localeCompare(b.name);
});

// ─── FeaturesCard ─────────────────────────────────────────────────────────────

const FeaturesCard = withStyles({
    root: {
        display: 'flex',
        marginBottom: '124px',
        padding: '0 160px 0 160px',
    },
})(({ classes, style, title, points, image, imageOnLeft, ...props }: any) => (
    <Paper
        elevation={0}
        classes={{ root: classes.root }}
        style={style}
        {...props}
    >
        {imageOnLeft && (
            <Box
                style={{
                    display: 'flex',
                    flex: '1 1 50%',
                    justifyContent: 'flex-start',
                }}
            >
                <img
                    src={image}
                    style={{
                        width: '548px',
                        height: '343px',
                        WebkitMaskImage:
                            '-webkit-linear-gradient(rgba(0,0,0,1), rgba(0,0,0,1), transparent)',
                    }}
                    alt={title}
                />
            </Box>
        )}
        <Box
            style={{
                display: 'flex',
                flex: '1 1 50%',
                padding: '24px 0 0 32px',
            }}
        >
            <Box>
                <Box
                    style={{
                        padding: '0 0.5rem 0 1rem',
                        display: 'flex',
                        flexDirection: 'row',
                    }}
                >
                    {title === 'Online Onboarding' ? (
                        <OnlineOnboarding />
                    ) : title === 'Digital Payroll' ? (
                        <DigitalPayroll />
                    ) : title === 'Smart Attendance' ? (
                        <SmartAttendance />
                    ) : title === 'Location Tracking' ? (
                        <LocationTracking />
                    ) : null}
                    <Typography
                        style={{
                            fontSize: '22px',
                            fontWeight: 700,
                            marginBottom: '8px',
                            paddingLeft: '8px',
                        }}
                    >
                        {title}
                    </Typography>
                </Box>
                <Box style={{ paddingLeft: '54px' }}>
                    {points?.map((point: string, index: number) => (
                        <Typography key={index}>
                            <li style={{ color: '#2b2b2b', fontSize: '18px' }}>
                                {point}
                            </li>
                        </Typography>
                    ))}
                </Box>
            </Box>
        </Box>
        {!imageOnLeft && (
            <Box style={{ display: 'flex', flex: '1 1 50%' }}>
                <img
                    src={image}
                    style={{
                        width: '548px',
                        height: '343px',
                        WebkitMaskImage:
                            '-webkit-linear-gradient(rgba(0,0,0,1), rgba(0,0,0,1), transparent)',
                    }}
                    alt={title}
                />
            </Box>
        )}
    </Paper>
));

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles(() => ({
    // Narrower cells once there are more than four, so the row still fits the card.
    otpInputStyle: {
        width: ({ otpLength }: { otpLength: number }) =>
            (otpLength > 4 ? '40px !important' : '48px !important'),
        height: '48px !important',
        borderRadius: '8px',
        border: '1px solid rgba(38, 50, 56, 0.16)',
        fontSize: '24px',
        fontWeight: 600,
        fontFamily: 'Nunito',
    },
    otpError: {
        border: '1px solid red !important',
    },
    countrySelect: {
        '& .MuiOutlinedInput-root': {
            '&:hover fieldset': { borderColor: '#1bb55c' },
            '&.Mui-focused fieldset': { borderColor: '#1bb55c' },
        },
        '& .MuiInputLabel-outlined.Mui-focused': { color: '#1bb55c' },
    },
    phoneInput: {
        '& .MuiOutlinedInput-root': {
            '&:hover fieldset': { borderColor: '#1bb55c' },
            '&.Mui-focused fieldset': { borderColor: '#1bb55c' },
        },
        '& .MuiInputLabel-outlined.Mui-focused': { color: '#1bb55c' },
    },
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

const validatePhone = (number: string, country: Country): ValidationState => {
    if (!number) return { isValid: false, message: '' };
    const fullNumber = `${country.dialCode}${number}`;
    try {
        const valid = isValidPhoneNumber(fullNumber, country.code);
        return valid
            ? { isValid: true, message: '' }
            : {
                  isValid: false,
                  message: `Enter a valid ${country.name} mobile number`,
              };
    } catch {
        return {
            isValid: false,
            message: `Enter a valid ${country.name} mobile number`,
        };
    }
};

// Vendors that require an explicit consent acknowledgement before login.
const CONSENT_VENDOR = 'BLUEDART';

// OTP validation persists a token before consent is captured. This flag marks
// that login is still gated on consent, so a page reload logs the user out
// instead of slipping them into the app with an un-consented session.
const CONSENT_PENDING_KEY = 'CONSENT_PENDING';

// Seconds before the SMS OTP can be resent.
const RESEND_OTP_SECONDS = 60;

// Used until the backend reports the length it actually generated.
const DEFAULT_OTP_LENGTH = 4;

const readOtpLength = (resp: any): number =>
    resp?.otpLength ?? resp?.response?.otpLength ?? DEFAULT_OTP_LENGTH;

// Seconds before OTP-on-call can be requested again. Shorter than the SMS
// resend window because it is a different delivery channel, not a retry.
const CALL_OTP_COOLDOWN_SECONDS = 30;

// ─── Component ────────────────────────────────────────────────────────────────

interface LoginDispatchProps {
    dispatchfetchAppConfig: (
        onSuccess: (resp: any) => void,
        onError?: () => void,
    ) => void;
    dispatchGetConsentContent: (
        number: string,
        onSuccess: (resp: any) => void,
        onError: (err: any) => void,
    ) => void;
    dispatchCheckSsoEligible: (
        contactNumber: string,
        onSuccess: (resp: any) => void,
        onError: (err: any) => void,
    ) => void;
    dispatchSendOtp: (
        mobileNumber: string,
        countryCode: string,
        onSuccess: (resp: any) => void,
        onError: () => void,
    ) => void;
    dispatchSendOtpViaCall: (
        mobileNumber: string,
        countryCode: string,
        onSuccess: (resp: any) => void,
        onError: (err: any) => void,
    ) => void;
    dispatchValidateOtp: (
        mobileNumber: string,
        countryCode: string,
        otp: string,
        overwriteHeaders: Record<string, unknown>,
        loginFrom: string,
        onSuccess: (resp: any) => void,
        onError: () => void,
    ) => void;
    dispatchGetOrganisations: (
        id: string,
        onSuccess: (resp: any) => void,
        onError: (resp: any) => void,
    ) => void;
    dispatchStoreOrganisation: (data: any) => void;
}

type LoginProps = LoginDispatchProps & RouteComponentProps;

const Login: React.FC<LoginProps> = ({
    dispatchfetchAppConfig,
    dispatchGetConsentContent,
    dispatchCheckSsoEligible,
    dispatchSendOtp,
    dispatchSendOtpViaCall,
    dispatchValidateOtp,
    dispatchGetOrganisations,
    dispatchStoreOrganisation,
    history,
}) => {
    // Digits the backend actually minted. Falls back to the legacy length when the
    // response predates the field. Declared before useStyles, which sizes the cells from it.
    const [otpLength, setOtpLength] = useState(DEFAULT_OTP_LENGTH);

    const classes = useStyles({ otpLength });

    const [selectedCountry, setSelectedCountry] = useState<Country>(
        ALLOWED_COUNTRIES[0],
    );
    const [mobileNumber, setMobileNumber] = useState('');
    const [validation, setValidation] = useState<ValidationState>({
        isValid: false,
        message: '',
    });
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [hasOtpError, setHasOtpError] = useState(false);
    const [OTP, setOTP] = useState('');
    const [resendOtpStatus, setResendOtpStatus] = useState(false);
    const [disableInputs, setDisableInputs] = useState(false);
    const [timerCount, setTimer] = useState(RESEND_OTP_SECONDS);

    // OTP-on-call: the inbound number the user must dial, the modal that
    // surfaces it, and a cooldown so the option cannot be spammed.
    const [otpOnCallNumber, setOtpOnCallNumber] = useState<string | null>(null);
    const [otpOnCallOpen, setOtpOnCallOpen] = useState(false);
    const [isCallOtpLoading, setIsCallOtpLoading] = useState(false);
    const [callCooldown, setCallCooldown] = useState(0);

    // Interval handles kept in refs so re-renders and remounts cannot leak a
    // second ticker that double-decrements the counters.
    const otpIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const callIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Post-OTP processing (config + consent + org fetch) is in flight.
    const [isProcessing, setIsProcessing] = useState(false);
    const [ssoError, setSsoError] = useState<string | null>(null);
    const [showSsoButton, setShowSsoButton] = useState(false);
    // BlueDart consent gating.
    const [consentOpen, setConsentOpen] = useState(false);
    const [consentParas, setConsentParas] = useState<ConsentPara[]>([]);

    const clearOtpTimer = () => {
        if (otpIntervalRef.current) {
            clearInterval(otpIntervalRef.current);
            otpIntervalRef.current = null;
        }
    };

    const resetOtpTimer = () => {
        // Both the SMS and the call path arm this timer — clear any running
        // ticker first so the countdown never decrements twice per second.
        clearOtpTimer();
        setTimer(RESEND_OTP_SECONDS);
        setOTP('');
        setHasOtpError(false);
        setDisableInputs(true);
        otpIntervalRef.current = setInterval(() => {
            setTimer((prev) => {
                if (prev <= 1) {
                    clearOtpTimer();
                    setResendOtpStatus(true);
                    setDisableInputs(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    // The inbound number is bound to the number that was submitted, so any edit
    // to the country/phone invalidates it. The cooldown deliberately keeps
    // running — it rate-limits the user, not the number.
    const clearCallOtpState = () => {
        setOtpOnCallNumber(null);
        setOtpOnCallOpen(false);
    };

    const startCallCooldown = () => {
        if (callIntervalRef.current) clearInterval(callIntervalRef.current);
        setCallCooldown(CALL_OTP_COOLDOWN_SECONDS);
        callIntervalRef.current = setInterval(() => {
            setCallCooldown((prev) => {
                if (prev <= 1) {
                    if (callIntervalRef.current) {
                        clearInterval(callIntervalRef.current);
                        callIntervalRef.current = null;
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    // Requests the SMS OTP and reveals the OTP boxes. Shared by the initial
    // "Get OTP", the resend link and the modal's "SMS instead" action.
    const requestSmsOtp = useCallback(
        (context: string) => {
            dispatchSendOtp(
                mobileNumber,
                selectedCountry.dialCode,
                (resp: any) => {
                    setOtpLength(readOtpLength(resp));
                    setShowOtpInput(true);
                    resetOtpTimer();
                    setResendOtpStatus(false);
                },
                () => {
                    console.error(`LoginScreen: ${context} failed`);
                },
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatchSendOtp, mobileNumber, selectedCountry.dialCode],
    );

    const handleSendOtp = () => {
        if (!validation.isValid) return;
        dispatchCheckSsoEligible(
            mobileNumber,
            (resp: any) => {
                if (resp?.response?.eligible === true) {
                    setShowSsoButton(true);
                } else {
                    requestSmsOtp('Get OTP');
                }
            },
            () => {
                // SSO check failed — fall through to OTP
                requestSmsOtp('Get OTP');
            },
        );
    };

    const handleResendOtp = (e: React.MouseEvent) => {
        e.preventDefault();
        requestSmsOtp('Resend OTP');
    };

    const canRequestCallOtp =
        validation.isValid && !isCallOtpLoading && callCooldown === 0;

    // Alternate delivery channel: the backend generates the OTP and returns an
    // inbound number the user calls to receive it.
    const handleGetOtpViaCall = (e?: React.MouseEvent) => {
        e?.preventDefault();
        if (!canRequestCallOtp) return;
        setIsCallOtpLoading(true);

        dispatchSendOtpViaCall(
            mobileNumber,
            selectedCountry.dialCode,
            (resp: any) => {
                setIsCallOtpLoading(false);
                // Defensive: only act on an explicit success envelope.
                if (resp?.status?.code && resp.status.code !== 200) return;

                const number =
                    resp?.otpOnCallMobileNumber ??
                    resp?.response?.otpOnCallMobileNumber ??
                    null;

                if (!number) {
                    showMessage(
                        'Could not start OTP on call. Please try SMS.',
                        'error',
                    );
                    return;
                }

                setOtpLength(readOtpLength(resp));
                setOtpOnCallNumber(String(number));
                setOtpOnCallOpen(true);
                setShowOtpInput(true);
                resetOtpTimer();
                setResendOtpStatus(false);
                startCallCooldown();
            },
            () => {
                // The middleware already surfaces the backend message; adding
                // another toast here would double up.
                setIsCallOtpLoading(false);
                console.error('LoginScreen: Get OTP via call failed');
            },
        );
    };

    // Fetch organisation, persist it and route into the app. This is the final
    // step of login — for consent vendors it runs only after the user accepts.
    const proceedToApp = useCallback(() => {
        dispatchGetOrganisations(
            getLoginUser().organizationId,
            (resp: any) => {
                localStorage.setItem(
                    'ORG_INFO',
                    window.btoa(
                        unescape(encodeURIComponent(JSON.stringify(resp))),
                    ),
                );
                localStorage.setItem('org', resp.name);
                dispatchStoreOrganisation(resp);
                setHasOtpError(false);
                history.push(`/${getActiveInitRoute()}`);
            },
            () => {
                setHasOtpError(false);
                history.push(`/${getActiveInitRoute()}`);
            },
        );
    }, [dispatchGetOrganisations, dispatchStoreOrganisation, history]);

    // MPIN gate divert: when the login response armed the gate (captured at
    // OTP-success time, below), the MPIN step comes BEFORE entering the app.
    // Consent stays first — a consent decline logs out, so collecting an
    // MPIN before it would waste the step on a dying session.
    const continueLogin = useCallback(() => {
        if (isMpinPending()) {
            history.replace('/mpin');
            return;
        }
        proceedToApp();
    }, [history, proceedToApp]);

    const loginHandler = () => {
        if (OTP.length !== otpLength || isProcessing) return;
        setIsProcessing(true);
        dispatchValidateOtp(
            mobileNumber,
            selectedCountry.dialCode,
            OTP,
            {},
            'desktop',
            (userRes: any) => {
                // Arm/disarm the MPIN gate from the login response BEFORE
                // any consent branching, so even a mid-consent reload or a
                // manual URL is bounced back to /mpin by MainLayout.
                captureLoginMpin(userRes?.response);
                setHasOtpError(false);
                dispatchfetchAppConfig(
                    (configResp: any) => {
                        const vendorName: string =
                            configResp?.response?.configs?.vendorName ??
                            configResp?.response?.vendorName ??
                            '';
                        const role = userRes?.response?.userBO?.role ?? '0';

                        // Consent gate applies to BlueDart only; everyone else
                        // continues straight into the app.
                        if (
                            BD_INTERNAL_ROLES?.includes(role) ||
                            String(vendorName).toUpperCase() !== CONSENT_VENDOR
                        ) {
                            continueLogin();
                            return;
                        }

                        const contactNumber =
                            getLoginUser()?.contactNumber || mobileNumber;
                        dispatchGetConsentContent(
                            contactNumber,
                            (consentResp: any) => {
                                const paras: ConsentPara[] =
                                    consentResp?.response?.paras ?? [];
                                if (paras.length > 0) {
                                    // Mark login as gated on consent so a reload
                                    // before the user proceeds logs them out.
                                    localStorage.setItem(
                                        CONSENT_PENDING_KEY,
                                        'true',
                                    );
                                    setConsentParas(paras);
                                    setConsentOpen(true);
                                    // Hand off to the modal; reset the busy
                                    // flag so the modal owns the next step.
                                    setIsProcessing(false);
                                } else {
                                    // No consent content → nothing to gate on.
                                    continueLogin();
                                }
                            },
                            () => {
                                // Consent fetch failed — don't block the user.
                                continueLogin();
                            },
                        );
                    },
                    () => {
                        setIsProcessing(false);
                    },
                );
            },
            () => {
                setHasOtpError(true);
                setIsProcessing(false);
            },
        );
    };

    // User accepted the consent terms → resume the deferred login.
    const handleConsentProceed = () => {
        // Consent captured — clear the gate so the session is now valid.
        localStorage.removeItem(CONSENT_PENDING_KEY);
        setIsProcessing(true);
        continueLogin();
    };

    // User dismissed the consent modal → log out and reset to the login screen.
    const handleConsentClose = () => {
        setConsentOpen(false);
        setConsentParas([]);
        setIsProcessing(false);
        logout();
    };

    const handleCountryChange = (
        event: React.ChangeEvent<{ value: unknown }>,
    ) => {
        const code = event.target.value as string;
        const country = ALLOWED_COUNTRIES.find((c) => c.code === code);
        if (!country) return;
        setSelectedCountry(country);
        setMobileNumber('');
        setValidation({ isValid: false, message: '' });
        clearCallOtpState();
        if (showOtpInput) {
            setShowOtpInput(false);
            clearOtpTimer();
        }
    };

    const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.replace(/\D/g, '');
        clearOtpTimer();
        clearCallOtpState();
        setMobileNumber(value);
        if (showOtpInput) setShowOtpInput(false);
        if (showSsoButton) setShowSsoButton(false);
        setValidation(
            value.length > 0
                ? validatePhone(value, selectedCountry)
                : { isValid: false, message: '' },
        );
    };

    const handlePhoneBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        if (event.target.value) {
            setValidation(validatePhone(event.target.value, selectedCountry));
        }
    };

    useEffect(() => {
        if (getUserToken()) {
            if (localStorage.getItem(CONSENT_PENDING_KEY) === 'true') {
                logout();
                return;
            }
            // Half-finished MPIN step → resume it; never route into the app.
            if (isMpinPending()) {
                history.replace('/mpin');
                return;
            }
            history.push(`/${getActiveInitRoute()}`);
        }
        const ssoErr = sessionStorage.getItem('sso_error');
        if (ssoErr) {
            setSsoError(ssoErr);
            sessionStorage.removeItem('sso_error');
        }
        return () => {
            if (otpIntervalRef.current) clearInterval(otpIntervalRef.current);
            if (callIntervalRef.current) clearInterval(callIntervalRef.current);
        };
    }, []);

    return (
        <Box style={{ backgroundColor: '#fff' }}>
            {/* ── BlueDart consent gate ── */}
            <ConsentModal
                open={consentOpen}
                paras={consentParas}
                submitting={isProcessing}
                onProceed={handleConsentProceed}
                onClose={handleConsentClose}
            />

            {/* ── OTP on call ── */}
            <OtpOnCallModal
                open={otpOnCallOpen}
                callNumber={otpOnCallNumber ?? ''}
                fromNumber={`${selectedCountry.dialCode} ${mobileNumber}`}
                onClose={() => setOtpOnCallOpen(false)}
                onSmsInstead={() => {
                    setOtpOnCallOpen(false);
                    requestSmsOtp('Get OTP via SMS instead');
                }}
                otpLength={otpLength}
            />

            {/* ── Header ── */}
            <Box
                style={{
                    height: '15%',
                    padding: '4px 0 4px 100px',
                    boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.07)',
                }}
            >
                <Typography
                    style={{
                        fontSize: '32px',
                        fontWeight: 700,
                        color: '#43419e',
                    }}
                >
                    StaffPay
                </Typography>
            </Box>

            {/* ── Hero tagline ── */}
            <Box
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    margin: '0 auto',
                    width: '35%',
                }}
            >
                <Typography
                    style={{
                        fontSize: '28px',
                        color: '#43419e',
                        fontWeight: 700,
                        textAlign: 'center',
                    }}
                >
                    Intelligent Payroll Partner for Your Mobile Workforce
                </Typography>
            </Box>

            {/* ── Login form + image ── */}
            <Box
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    marginBottom: '0px',
                }}
            >
                <Box style={{ flex: '0 0 60%' }}>
                    <img
                        src={loginImage}
                        style={{ width: '906px', height: '540px' }}
                        alt="login"
                    />
                </Box>

                <Box
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        margin: '80px auto',
                        padding: '0 120px 0 0',
                        minWidth: '340px',
                    }}
                >
                    <Typography
                        style={{
                            fontSize: '30px',
                            fontWeight: 600,
                            marginBottom: '24px',
                        }}
                    >
                        Welcome to StaffPay
                    </Typography>

                    {/* Country selector */}
                    <FormControl
                        variant="outlined"
                        fullWidth
                        className={classes.countrySelect}
                        style={{ marginBottom: '16px' }}
                    >
                        <InputLabel>Country</InputLabel>
                        <Select
                            value={selectedCountry.code}
                            onChange={handleCountryChange}
                            label="Country"
                            disabled={disableInputs}
                            renderValue={(value) => {
                                const c = ALLOWED_COUNTRIES.find(
                                    (x) => x.code === value,
                                );
                                return c ? (
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        style={{ gap: '8px' }}
                                    >
                                        <span
                                            style={{
                                                fontSize: '20px',
                                                lineHeight: 1,
                                            }}
                                        >
                                            {c.flag}
                                        </span>
                                        <span>{c.name}</span>
                                        <span
                                            style={{
                                                color: '#666',
                                                fontSize: '14px',
                                            }}
                                        >
                                            {c.dialCode}
                                        </span>
                                    </Box>
                                ) : null;
                            }}
                        >
                            {ALLOWED_COUNTRIES.map((country) => (
                                <MenuItem
                                    key={country.code}
                                    value={country.code}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        style={{ gap: '8px', width: '100%' }}
                                    >
                                        <span
                                            style={{
                                                fontSize: '20px',
                                                lineHeight: 1,
                                            }}
                                        >
                                            {country.flag}
                                        </span>
                                        <span style={{ flexGrow: 1 }}>
                                            {country.name}
                                        </span>
                                        <span
                                            style={{
                                                color: '#666',
                                                fontSize: '14px',
                                            }}
                                        >
                                            {country.dialCode}
                                        </span>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Phone number input */}
                    <TextField
                        fullWidth
                        variant="outlined"
                        label="Mobile Number"
                        value={mobileNumber}
                        disabled={disableInputs}
                        onChange={handlePhoneChange}
                        onBlur={handlePhoneBlur}
                        error={!!validation.message}
                        helperText={validation.message || ' '}
                        inputProps={{ maxLength: 15, inputMode: 'numeric' }}
                        className={classes.phoneInput}
                        style={{ marginBottom: '4px' }}
                    />

                    {/* OTP section */}
                    {showOtpInput && (
                        <Fade in={showOtpInput} timeout={800}>
                            <Box>
                                <OtpInput
                                    inputStyle={classes.otpInputStyle}
                                    numInputs={otpLength}
                                    hasErrored={hasOtpError}
                                    errorStyle={classes.otpError}
                                    onChange={(otp: string) => {
                                        setOTP(otp);
                                        if (otp.length !== otpLength)
                                            setHasOtpError(false);
                                    }}
                                    separator={<span>&nbsp;&nbsp;</span>}
                                    isInputNum={true}
                                    shouldAutoFocus
                                    value={OTP}
                                />

                                <Box
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '12px',
                                        margin: '12px 0 12px 0',
                                        minHeight: '20px',
                                    }}
                                >
                                    <Typography
                                        style={{
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: hasOtpError
                                                ? '#d32f2f'
                                                : '#555',
                                        }}
                                    >
                                        {hasOtpError &&
                                            'Incorrect OTP. Please try again.'}
                                        {!hasOtpError &&
                                            !resendOtpStatus &&
                                            `Resend OTP in ${('00' + timerCount).slice(-2)}s`}
                                        {!hasOtpError && resendOtpStatus && (
                                            <Link
                                                href="#"
                                                onClick={handleResendOtp}
                                            >
                                                Resend OTP
                                            </Link>
                                        )}
                                    </Typography>

                                    {/* A different delivery channel, not a
                                        resend — available even while the resend
                                        countdown is running. */}
                                    <Typography
                                        style={{
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {canRequestCallOtp ? (
                                            <Link
                                                href="#"
                                                onClick={handleGetOtpViaCall}
                                            >
                                                Get OTP via call
                                            </Link>
                                        ) : (
                                            <span style={{ color: '#90a4ae' }}>
                                                {isCallOtpLoading
                                                    ? 'Requesting call…'
                                                    : `Get OTP via call in ${('00' + callCooldown).slice(-2)}s`}
                                            </span>
                                        )}
                                    </Typography>
                                </Box>

                                {/* Persistent reminder so the number survives
                                    the modal being dismissed. */}
                                {otpOnCallNumber && !otpOnCallOpen && (
                                    <Box
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            backgroundColor: '#eef2ff',
                                            border: '1px solid #c5cae9',
                                            borderRadius: '8px',
                                            padding: '10px 12px',
                                            marginBottom: '16px',
                                            gap: '8px',
                                        }}
                                    >
                                        <PhoneInTalkRoundedIcon
                                            style={{
                                                color: '#43419e',
                                                fontSize: '20px',
                                            }}
                                        />
                                        <Typography
                                            style={{
                                                flexGrow: 1,
                                                fontSize: '13px',
                                                color: '#1a237e',
                                                fontWeight: 600,
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            Call{' '}
                                            <strong>
                                                {formatCallNumber(
                                                    otpOnCallNumber,
                                                )}
                                            </strong>{' '}
                                            to get your OTP
                                        </Typography>
                                        <Button
                                            size="small"
                                            onClick={() =>
                                                copyCallNumber(otpOnCallNumber)
                                            }
                                            startIcon={
                                                <FileCopyOutlinedIcon
                                                    style={{ fontSize: '16px' }}
                                                />
                                            }
                                            style={{
                                                textTransform: 'none',
                                                fontSize: '13px',
                                                fontWeight: 700,
                                                color: '#43419e',
                                                flexShrink: 0,
                                            }}
                                            aria-label="copy number"
                                        >
                                            Copy
                                        </Button>
                                    </Box>
                                )}

                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={
                                        OTP.length !== otpLength || isProcessing
                                    }
                                    fullWidth
                                    onClick={loginHandler}
                                    style={{ height: '48px', fontSize: '18px' }}
                                    disableElevation
                                >
                                    {isProcessing ? 'Logging in…' : 'Login'}
                                </Button>
                            </Box>
                        </Fade>
                    )}

                    {!showOtpInput && !showSsoButton && (
                        <Fade
                            in={!showOtpInput && !showSsoButton}
                            timeout={800}
                        >
                            <Box>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    disableElevation
                                    onClick={handleSendOtp}
                                    style={{ height: '48px', fontSize: '16px' }}
                                    disabled={!validation.isValid}
                                >
                                    Get OTP
                                </Button>

                                {/* Alternate channel for users whose SMS never
                                    arrives (DND / DLT filtering / poor coverage). */}
                                <Button
                                    variant="text"
                                    fullWidth
                                    onClick={handleGetOtpViaCall}
                                    disabled={!canRequestCallOtp}
                                    style={{
                                        height: '40px',
                                        marginTop: '8px',
                                        fontSize: '14px',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        color: canRequestCallOtp
                                            ? '#43419e'
                                            : undefined,
                                    }}
                                    startIcon={
                                        isCallOtpLoading ? (
                                            <CircularProgress
                                                size={16}
                                                color="inherit"
                                            />
                                        ) : (
                                            <PhoneInTalkRoundedIcon />
                                        )
                                    }
                                >
                                    {isCallOtpLoading
                                        ? 'Requesting call…'
                                        : callCooldown > 0
                                          ? `Get OTP via call in ${('00' + callCooldown).slice(-2)}s`
                                          : 'Get OTP via call'}
                                </Button>
                            </Box>
                        </Fade>
                    )}

                    {/* ── SSO error message ── */}
                    {ssoError && (
                        <Typography
                            style={{
                                color: '#d32f2f',
                                fontSize: '13px',
                                marginBottom: '8px',
                                textAlign: 'center',
                            }}
                        >
                            {ssoError}
                        </Typography>
                    )}

                    {/* ── Google SSO button (shown only for @loadshare.net users) ── */}
                    {showSsoButton && (
                        <Fade in={showSsoButton} timeout={400}>
                            <Box>
                                <Box
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        backgroundColor: '#eef2ff',
                                        border: '1px solid #c5cae9',
                                        borderRadius: '8px',
                                        padding: '12px 16px',
                                        margin: '12px 0',
                                        gap: '8px',
                                    }}
                                >
                                    <Typography
                                        style={{
                                            fontSize: '16px',
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        ⓘ
                                    </Typography>
                                    <Typography
                                        style={{
                                            fontSize: '14px',
                                            color: '#1a237e',
                                            fontWeight: 600,
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        This number is linked to a Loadshare
                                        account. Continue to Gmail sign-in.
                                    </Typography>
                                </Box>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    disableElevation
                                    onClick={() => {
                                        window.location.href = `${API_URL}/auth/sso/redirect?provider=google`;
                                    }}
                                    style={{
                                        height: '48px',
                                        fontSize: '15px',
                                        borderColor: '#dadce0',
                                        color: '#3c4043',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                    }}
                                    startIcon={
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 18 18"
                                        >
                                            <path
                                                fill="#4285F4"
                                                d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"
                                            />
                                            <path
                                                fill="#34A853"
                                                d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2.04a4.8 4.8 0 0 1-7.18-2.53H1.83v2.07A8 8 0 0 0 8.98 17z"
                                            />
                                            <path
                                                fill="#FBBC05"
                                                d="M4.5 10.49a4.8 4.8 0 0 1 0-3.03V5.39H1.83a8 8 0 0 0 0 7.17l2.67-2.07z"
                                            />
                                            <path
                                                fill="#EA4335"
                                                d="M8.98 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A8 8 0 0 0 1.83 5.4L4.5 7.46a4.77 4.77 0 0 1 4.48-3.88z"
                                            />
                                        </svg>
                                    }
                                >
                                    Sign in with Google
                                </Button>
                            </Box>
                        </Fade>
                    )}
                </Box>
            </Box>

            {/* ── Feature chips ── */}
            <Box
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    margin: '0 100px',
                    justifyContent: 'space-evenly',
                }}
            >
                {[
                    { text: 'Online Onboarding' },
                    { text: 'Digital Payroll' },
                    { text: 'Smart Attendance' },
                    { text: 'Location Tracking' },
                    { text: 'Staff App' },
                ].map((data, index) => (
                    <Box
                        key={index}
                        style={{
                            display: 'flex',
                            borderRadius: '8px',
                            boxShadow: '0 2px 14px 0 rgba(0, 0, 0, 0.12)',
                            backgroundColor: '#fff',
                            height: '5.625rem',
                            flex: '0 0 18%',
                            marginRight: '1rem',
                            alignItems: 'center',
                        }}
                    >
                        <Box style={{ padding: '0 0.5rem 0 1rem' }}>
                            {data.text === 'Online Onboarding' ? (
                                <OnlineOnboarding />
                            ) : data.text === 'Digital Payroll' ? (
                                <DigitalPayroll />
                            ) : data.text === 'Smart Attendance' ? (
                                <SmartAttendance />
                            ) : data.text === 'Location Tracking' ? (
                                <LocationTracking />
                            ) : data.text === 'Staff App' ? (
                                <StaffWithApp />
                            ) : null}
                        </Box>
                        <Box>
                            <Typography
                                style={{
                                    color: '#2b2b2b',
                                    fontWeight: 700,
                                    fontSize: '18px',
                                    paddingRight: '8px',
                                }}
                            >
                                {data.text}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>

            {/* ── Feature detail cards ── */}
            <Box style={{ margin: '74px auto 100px auto' }}>
                <FeaturesCard
                    title="Online Onboarding"
                    points={[
                        'Basic Info and KYC Verification',
                        'Contract Signing and ID Card Generation',
                        'Learning Videos',
                        'Grievance Management',
                    ]}
                    image={featureImage1}
                    imageOnLeft={false}
                />
                <FeaturesCard
                    title="Digital Payroll"
                    points={[
                        'Manage Earnings, Bonus, Expense and Deductions',
                        'Manage Advances and Payouts',
                        'Bulk Upload your Expenses and Payments',
                    ]}
                    image={featureImage2}
                    imageOnLeft={true}
                />
                <FeaturesCard
                    title="Smart Attendance"
                    points={[
                        'Selfie Based Approval',
                        'Delivery Hub Geofencing based Approval',
                        'Delivery Hub Geofencing based Auto-Approval',
                        'Manual Marking',
                    ]}
                    image={featureImage3}
                    imageOnLeft={false}
                />
                <FeaturesCard
                    title="Location Tracking"
                    points={[
                        'Realtime Staff Tracking using GPS',
                        'Distance Report',
                    ]}
                    image={featureImage4}
                    imageOnLeft={true}
                />

                {/* Staff App section */}
                <Paper
                    elevation={0}
                    style={{ display: 'flex', padding: '0 160px 0 160px' }}
                >
                    <Box
                        style={{
                            display: 'flex',
                            flex: '1 1 50%',
                            padding: '24px 0 0 12px',
                        }}
                    >
                        <Box>
                            <Box
                                style={{
                                    padding: '0 0.5rem 0 1rem',
                                    display: 'flex',
                                    flexDirection: 'row',
                                }}
                            >
                                <StaffWithApp />
                                <Typography
                                    style={{
                                        fontSize: '22px',
                                        fontWeight: 700,
                                        marginBottom: '16px',
                                        paddingLeft: '8px',
                                    }}
                                >
                                    Staff App
                                </Typography>
                            </Box>
                            <Box style={{ paddingRight: '20px' }}>
                                <Typography
                                    style={{
                                        color: '#2b2b2b',
                                        fontSize: '18px',
                                        fontWeight: 600,
                                    }}
                                >
                                    Your staff can check their Earnings, Manage
                                    their KYC Documents and Mark Attendance
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Box style={{ display: 'flex', flex: '1 1 50%' }}>
                        <img
                            src={deAppImages}
                            style={{
                                width: '548px',
                                height: '343px',
                                paddingLeft: '12px',
                                WebkitMaskImage:
                                    '-webkit-linear-gradient(rgba(0,0,0,1), rgba(0,0,0,1), transparent)',
                            }}
                            alt="staff-app"
                        />
                    </Box>
                </Paper>
            </Box>

            {/* ── Footer contact ── */}
            <Box style={{ padding: '80px 100px', backgroundColor: '#ededf6' }}>
                <Box
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box
                        style={{
                            display: 'flex',
                            flex: '1 1 50%',
                            flexDirection: 'column',
                        }}
                    >
                        <Typography
                            style={{ fontSize: '32px', fontWeight: 600 }}
                        >
                            StaffPay
                        </Typography>
                        <Typography
                            style={{ fontSize: '20px', paddingRight: '25%' }}
                        >
                            99, 3rd A Cross Rd, 2nd Stage, Naagarbhaavi,
                            Bengaluru, Karnataka 560072
                        </Typography>
                    </Box>
                    <Box
                        style={{
                            display: 'flex',
                            flex: '1 1 50%',
                            alignItems: 'flex-end',
                            justifyContent: 'flex-end',
                            flexDirection: 'column',
                        }}
                    >
                        <Box
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                            }}
                        >
                            <PhoneRoundedIcon />
                            <Typography
                                style={{ fontSize: '20px', marginLeft: '4px' }}
                            >
                                E-mail: <strong>dev@staffpay.in</strong>
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* ── Footer copyright ── */}
            <Box
                style={{
                    backgroundColor: '#fafaff',
                    padding: '30px 100px',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                }}
            >
                <Typography style={{ fontSize: '14px' }}>
                    Copyrighted @ {new Date().getFullYear()}.
                </Typography>
                <Box
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                    }}
                >
                    <Typography
                        style={{
                            fontSize: '14px',
                            textDecoration: 'underline',
                            color: '#2777fa',
                        }}
                    >
                        <Link
                            href="#"
                            onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                history.push('/privacy');
                            }}
                        >
                            Privacy Policy
                        </Link>
                    </Typography>
                    <Typography
                        style={{
                            fontSize: '14px',
                            textDecoration: 'underline',
                            marginLeft: '8px',
                        }}
                    >
                        <Link
                            href="#"
                            onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                history.push('/terms');
                            }}
                        >
                            Terms and Conditions
                        </Link>
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

// ─── Redux ────────────────────────────────────────────────────────────────────

const mapDispatchToProps = (dispatch: any) => ({
    dispatchCheckSsoEligible: (
        contactNumber: string,
        onSuccess: (resp: any) => void,
        onError: (err: any) => void,
    ) => dispatch(checkSsoEligible(contactNumber, onSuccess, onError)),

    dispatchSendOtp: (
        mobileNumber: string,
        countryCode: string,
        onSuccess: (resp: any) => void,
        onError: () => void,
    ) => dispatch(sendOTP(mobileNumber, countryCode, onSuccess, onError)),

    dispatchSendOtpViaCall: (
        mobileNumber: string,
        countryCode: string,
        onSuccess: (resp: any) => void,
        onError: (err: any) => void,
    ) =>
        dispatch(sendOTPViaCall(mobileNumber, countryCode, onSuccess, onError)),

    dispatchValidateOtp: (
        mobileNumber: string,
        countryCode: string,
        otp: string,
        overwriteHeaders: Record<string, unknown>,
        loginFrom: string,
        onSuccess: (resp: any) => void,
        onError: () => void,
    ) =>
        dispatch(
            validateOTP(
                {
                    number: mobileNumber,
                    countryCode,
                    otp,
                    overwriteHeaders,
                    loginFrom,
                },
                onSuccess,
                onError,
            ),
        ),

    dispatchfetchAppConfig: (
        onSuccess: (resp: any) => void,
        onError?: () => void,
    ) => dispatch(fetchAppConfig(onSuccess, onError)),

    dispatchGetConsentContent: (
        number: string,
        onSuccess: (resp: any) => void,
        onError: (err: any) => void,
    ) =>
        dispatch(
            getConsentContent(
                { number, source: 'desktop' },
                onSuccess,
                onError,
            ),
        ),

    dispatchGetOrganisations: (
        id: string,
        onSuccess: (resp: any) => void,
        onError: (resp: any) => void,
    ) => dispatch(GetOrganisations(id, onSuccess, onError)),

    dispatchStoreOrganisation: (data: any) => dispatch(StoreOrganisation(data)),
});

const ConnectedLogin = connect(null, mapDispatchToProps)(Login as any);
export default withRouter(ConnectedLogin as any);
